import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Users,
  Package,
  Building2,
  BarChart3,
  Settings,
  Newspaper,
  Bell,
} from "lucide-react";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalIndustries: 0,
    totalServices: 0,
    totalNews:0,
    totalUsers: 0,  
    activeProducts: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load user data from localStorage
    const userData = localStorage.getItem("adminUser");
    if (userData) {
      setUser(JSON.parse(userData));
    }

    // Simulate loading stats
    setTimeout(() => {
      setStats({
        totalProducts: 12,
        totalIndustries: 5,
        totalServices: 6,
        totalNews: 3,
        totalUsers: 48,
        activeProducts: 10,
      });
      setLoading(false);
    }, 1000);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name || "Admin"}! 👋
          </h2>
          <p className="text-gray-600">
            Here's what's happening with your business today
          </p>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {/* Products Stat */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition border border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">
                      Total Products
                    </p>
                    <p className="text-4xl font-bold text-gray-900">
                      {stats.totalProducts}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Package className="text-blue-600" size={24} />
                  </div>
                </div>
                <p className="text-xs text-green-600 font-semibold">
                  ↑ {stats.activeProducts} active
                </p>
              </div>

              {/* Industries Stat */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition border border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">
                      Industries
                    </p>
                    <p className="text-4xl font-bold text-gray-900">
                      {stats.totalIndustries}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Building2 className="text-green-600" size={24} />
                  </div>
                </div>
                <p className="text-xs text-green-600 font-semibold">
                  All active
                </p>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition border border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">
                      Total Services
                    </p>

                    <p className="text-4xl font-bold text-gray-900">
                      {stats.totalServices}
                    </p>
                  </div>

                  <div className="p-3 bg-cyan-100 rounded-lg">
                    <Package className="text-cyan-600" size={24} />
                  </div>
                </div>

                <p className="text-xs text-cyan-600 font-semibold">
                  All Active
                </p>
              </div>

                       
                           {/* News Stat */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition border border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">
                      Total News & Events
                    </p>
                              
                    <p className="text-4xl font-bold text-gray-900">
                      {stats.totalNews}
                    </p>
                  </div>

                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Newspaper className="text-purple-600" size={24} />
                  </div>
                </div>

                <p className="text-xs text-purple-600 font-semibold">
                  All Active
                </p>
              </div>



              {/* Users Stat */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition border border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">
                      Total Users
                    </p>
                    <p className="text-4xl font-bold text-gray-900">
                      {stats.totalUsers}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Users className="text-purple-600" size={24} />
                  </div>
                </div>
                <p className="text-xs text-purple-600 font-semibold">
                  This month
                </p>
              </div>

              {/* Performance Stat */}
              <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition border border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-gray-600 text-sm font-medium mb-1">
                      Performance
                    </p>
                    <p className="text-4xl font-bold text-gray-900">98%</p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <BarChart3 className="text-orange-600" size={24} />
                  </div>
                </div>
                <p className="text-xs text-green-600 font-semibold">
                  ↑ 2% from last week
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
              {/* Management Sections */}
              <div className="lg:col-span-2 space-y-6">
                {/* Products Section */}
                <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">
                      Products Management
                    </h3>
                    <Package className="text-blue-600" size={28} />
                  </div>
                  <p className="text-gray-600 mb-6">
                    Manage your products, features, and capabilities
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate("/products")}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition"
                    >
                      View Products
                    </button>
                    <button
                      onClick={() => navigate("/products")}
                      className="px-6 py-3 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold rounded-lg transition"
                    >
                      Add New
                    </button>
                  </div>
                </div>

                {/* Industries Section */}
                <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">
                      Industries Management
                    </h3>
                    <Building2 className="text-green-600" size={28} />
                  </div>
                  <p className="text-gray-600 mb-6">
                    Manage industry categories and their solutions
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate("/industries")}
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition"
                    >
                      View Industries
                    </button>
                    <button
                      onClick={() => navigate("/industries")}
                      className="px-6 py-3 border-2 border-green-600 text-green-600 hover:bg-green-50 font-semibold rounded-lg transition"
                    >
                      Add New
                    </button>
                  </div>
                </div>


                <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">
                      Services Management
                    </h3>

                    <Package className="text-cyan-600" size={28} />
                  </div>

                  <p className="text-gray-600 mb-6">
                    Manage your AI services and offerings
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate("/services")}
                      className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition"
                    >
                      View Services
                    </button>

                    <button
                      onClick={() => navigate("/services")}
                      className="px-6 py-3 border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 font-semibold rounded-lg transition"
                    >
                      Add New
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition border border-gray-100">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">
                      News & Events Management
                    </h3>

                    <Newspaper className="text-purple-600" size={28} />
                  </div>

                  <p className="text-gray-600 mb-6">
                    Manage your News & Events
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate("/news")}
                      className="px-6 py-3 bg-purple-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition"
                    >
                      View News & Events
                    </button>

                    <button
                      onClick={() => navigate("/news")}
                      className="px-6 py-3 border-2 border-purple-600 text-purple-600 hover:bg-purple-50 font-semibold rounded-lg transition"
                    >
                      Add New
                    </button>
                  </div>
                </div>


              </div>

              {/* Settings & Info */}
              <div className="space-y-6">
                {/* Quick Settings */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Settings size={20} />
                    Quick Settings
                  </h4>
                  <div className="space-y-3">
                    <button className="w-full px-4 py-3 bg-white hover:bg-gray-50 text-gray-900 font-medium rounded-lg border border-gray-200 transition text-left flex items-center justify-between">
                      <span>Account Settings</span>
                      <span>→</span>
                    </button>
                    <button className="w-full px-4 py-3 bg-white hover:bg-gray-50 text-gray-900 font-medium rounded-lg border border-gray-200 transition text-left flex items-center justify-between">
                      <span>Security</span>
                      <span>→</span>
                    </button>
                    <button className="w-full px-4 py-3 bg-white hover:bg-gray-50 text-gray-900 font-medium rounded-lg border border-gray-200 transition text-left flex items-center justify-between">
                      <span>API Keys</span>
                      <span>→</span>
                    </button>
                  </div>
                </div>

                {/* System Status */}
                <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                  <h4 className="font-bold text-gray-900 mb-4">
                    System Status
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">API Server</span>
                      <span className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs font-semibold text-green-600">
                          Online
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Database</span>
                      <span className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs font-semibold text-green-600">
                          Connected
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        Response Time
                      </span>
                      <span className="text-xs font-semibold text-gray-900">
                        125ms
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Recent Activity
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900">
                      Product Updated
                    </p>
                    <p className="text-sm text-gray-600">MIST System</p>
                  </div>
                  <span className="text-xs text-gray-500">2 hours ago</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900">
                      Industry Created
                    </p>
                    <p className="text-sm text-gray-600">Healthcare Sector</p>
                  </div>
                  <span className="text-xs text-gray-500">4 hours ago</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <p className="font-semibold text-gray-900">
                      Login Successful
                    </p>
                    <p className="text-sm text-gray-600">Admin Portal</p>
                  </div>
                  <span className="text-xs text-gray-500">Just now</span>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-12 py-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center text-gray-600 text-sm">
          <p>&copy; 2024 Admin Panel. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AdminDashboard;
